/**
 * Who the browser telemetry is about, and which page of the app it came from
 * (#1345).
 *
 * The landing page view is named and attributed inline, by the snippet's
 * `onInit` in Views/Shared/_allAppsCommonHeadSection.cshtml. That is not a
 * stylistic choice: the snippet queues `onInit` ahead of its own first
 * `trackPageView`, so a telemetry initializer registered there reaches that page
 * view, and one registered from this bundle would be queued behind it and miss
 * it. What is left for here is everything the shell cannot see — the home app's
 * hash routes, and a sign-in that never reloads the page.
 */

type TelemetryProperties = { [key: string]: any };

export interface IAppInsights {
    trackEvent(event: { name: string; iKey?: string }, customProperties?: TelemetryProperties): void;
    trackPageView(pageView?: { name?: string; uri?: string; properties?: TelemetryProperties }): void;
    setAuthenticatedUserContext(authenticatedUserId: string, accountId?: string, storeInCookie?: boolean): void;
    clearAuthenticatedUserContext(): void;
}

declare global {
    interface Window {
        // Optional because the snippet that puts it there is rendered only where
        // an App Insights connection string is configured — see the @if guarding
        // it in Views/Shared/_allAppsCommonHeadSection.cshtml.
        appInsights?: IAppInsights;
    }
}

export function gpAppInsights(): IAppInsights | undefined {
    return typeof window === 'undefined' ? undefined : window.appInsights;
}

/**
 * Application Insights silently declines an authenticated id containing any of
 * `,;=| `, recording the item as anonymous rather than erroring. The shell
 * inlines the same substitution, because it has to run before this bundle
 * exists — if you change one, change both.
 */
export function gpTelemetryUserId(email: string): string {
    return (email || '').replace(/[,;=| ]/g, '_');
}

/**
 * The name a page view gets: the server path the shell rendered, plus the home
 * app's hash route where there is one — `/#/plans`, `/projects/{x}/edit`.
 */
export function gpRouteName(basePath: string, hashRoute?: string): string {
    const base = basePath || '/';

    // ngRoute reports the app's root as "/", which names the same page the base
    // path already names. Appending it would call the landing page "/#/" while
    // the page view the shell already sent for it says "/".
    if (!hashRoute || hashRoute === '/') {
        return base;
    }

    return base + '#' + (hashRoute.charAt(0) === '/' ? hashRoute : '/' + hashRoute);
}

let seenFirstRoute = false;
let shellRouteName: string | undefined;

function shellRoute(): string {
    if (shellRouteName === undefined) {
        shellRouteName = (typeof __gameplan_route === 'string' && __gameplan_route) || '/';
    }

    return shellRouteName;
}

/** Records a hash navigation of the home app, and names what follows it. */
export function gpTrackRoute(hashRoute: string): void {
    const route = gpRouteName(shellRoute(), hashRoute);

    // Read by the shell's telemetry initializer on every item it sends, so this
    // assignment is what keeps traces, events and exceptions raised after a
    // navigation attributed to the page the user is on rather than the one they
    // arrived on.
    //
    // Through `window`, because unqualified it is a ReferenceError from this
    // module's strict-mode code wherever the page declared no such global —
    // which GamePlanDevServer/Views/home.html, the local stand-in for the
    // cshtml, does not.
    window.__gameplan_route = route;

    const firstRoute = !seenFirstRoute;
    seenFirstRoute = true;

    // The shell already sent a page view for the load, named from the path
    // alone. Where the route the app settled on says no more than that name did,
    // sending it again would double every page-view count the home app produces;
    // where it says more — "/#/account" opened from a bookmark — the shell's is
    // the name that was short, and this is the one worth having.
    if (firstRoute && route === shellRoute()) {
        return;
    }

    const appInsights = gpAppInsights();

    if (appInsights && appInsights.trackPageView) {
        appInsights.trackPageView({ name: route, uri: window.location.href });
    }
}

// Who this has already named, so that a later callback with no userId can be
// read as a sign-out rather than as a page nobody ever signed in on. Keyed on
// the instance rather than held in a module flag so that specs sharing this
// module do not inherit each other's answer.
const named = new WeakSet<IAppInsights>();

/**
 * Names the signed-in user on everything sent from here on, and unnames them
 * when they sign out.
 *
 * The absence of a userId is the sign-out signal, guarded by having named
 * someone first. An anonymous session never names anyone, so it never clears —
 * which is what leaves the identity the shell seeded at render time alone while
 * the app's own authorisation check is still in flight.
 *
 * Why not the reported status: see the call site in
 * app-common/services/telemetry-service.ts, which is the only caller.
 */
export function gpSetTelemetryUser(
    email: string,
    userId: string,
    // Injected only so a spec can pin the argument ORDER below. That order is
    // the correction this whole change carries — reversed, it compiles, passes
    // every other spec, and silently splits each user into two in Users,
    // Cohorts and Retention. Two adjacent strings deserve a test.
    ai: IAppInsights | undefined = gpAppInsights()): void {

    const appInsights = ai;

    if (!appInsights) {
        return;
    }

    if (userId && appInsights.setAuthenticatedUserContext) {
        // userId first. Email is mutable in this product, and App Insights'
        // Users, Cohorts and Retention all count distinct user_AuthenticatedId —
        // an email there splits one person in two the moment they change it.
        //
        // storeInCookie stays false: every render seeds this again, so a cookie
        // would add nothing but a way for one user's id to outlive their session
        // on a shared browser.
        appInsights.setAuthenticatedUserContext(userId, gpTelemetryUserId(email) || undefined, false);
        named.add(appInsights);
    } else if (!userId && named.has(appInsights) && appInsights.clearAuthenticatedUserContext) {
        appInsights.clearAuthenticatedUserContext();
        named.delete(appInsights);
    }
}
